import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Typography,
  Paper,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, CheckCircleOutline as ApproveIcon } from '@mui/icons-material';
import { draftRatingApi, DraftRatingState, DraftRatingResults, AiRatingReview, AiRatingSkill } from '../services/api';
import InfoTip from '../components/common/InfoTip';

const errorText = (err: any, fallback: string): string =>
  err?.response?.data?.error || err?.message || fallback;

const SEND_OPTIONS: Array<[string, string, string]> = [
  ['as_is', 'Send as is', 'You would send this draft without changing anything.'],
  ['small_edit', 'Small edit', 'A word or a sentence needs fixing before you would send it.'],
  ['rewrite', 'Rewrite', 'The idea is usable but most of it needs rewriting.'],
  ['unsafe', 'Unsafe / wrong', 'Wrong, misleading or unsafe: it should not be sent at all.'],
];

const SEND_LABELS: Record<string, string> = Object.fromEntries(SEND_OPTIONS.map(([v, l]) => [v, l]));

interface Props {
  initial: DraftRatingState;
}

type Examples = AiRatingReview['examples'];

const KIND_LABEL: Record<string, string> = {
  voice: 'Writing style',
  compression_rule: 'Compression',
  knowledge: 'Clinic knowledge',
  skill: 'Topic skill',
};

const SkillCard: React.FC<{ s: AiRatingSkill; onDone: () => void }> = ({ s, onDone }) => {
  const [text, setText] = useState(s.text);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const edited = text.trim() !== s.text.trim();
  const staffEdit = s.key.startsWith('example_edit_');

  const act = async (action: 'save' | 'approve' | 'reject') => {
    if (action === 'reject' && !window.confirm('Set this reply aside? The AI will not use it as an example.')) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await draftRatingApi.reviewAction(s.id, action, edited && action !== 'reject' ? text : undefined);
      if (res.data.unchanged) setNotice('This version is already approved. Change the text first to approve a new version.');
      else onDone();
      if (res.data.warning) setNotice(res.data.warning);
    } catch (err: any) {
      setError(errorText(err, 'The change could not be saved.'));
    } finally {
      setBusy(false);
    }
  };

  const label = staffEdit ? 'Example reply' : s.feature === 'reply.knowledge' ? KIND_LABEL.knowledge : KIND_LABEL[s.kind] || s.kind;
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{s.name}</Typography>
        <Chip size="small" label={label} variant="outlined" />
        <Chip
          size="small"
          label={s.status === 'approved' ? `Approved v${s.version}` : `Waiting for approval v${s.version}`}
          color={s.status === 'approved' ? 'success' : 'default'}
          variant={s.status === 'approved' ? 'filled' : 'outlined'}
        />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {s.status === 'approved'
          ? `Approved${s.reviewedBy ? ` by ${s.reviewedBy}` : ''}${s.reviewedAt ? ` on ${new Date(s.reviewedAt).toLocaleString()}` : ''}. Edit it and press Approve to make the edited text the new approved version.`
          : `Written by ${s.author || 'unknown'}${s.createdAt ? ` on ${new Date(s.createdAt).toLocaleString()}` : ''}.`}
        {s.description ? ` Used for: ${s.description}` : ''}
      </Typography>
      {s.warnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>{s.warnings.join(' ')} Approved text reaches every patient's answers, so remove it first.</Alert>
      )}
      {s.aiDraft && (
        <Box sx={{ bgcolor: 'action.hover', p: 1.5, borderRadius: 1, mb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>The AI draft it started from</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{s.aiDraft}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontWeight: 600 }}>What staff sent instead (names, phone numbers and dates removed):</Typography>
        </Box>
      )}
      <TextField
        fullWidth
        multiline
        minRows={4}
        maxRows={18}
        size="small"
        value={text}
        disabled={busy || s.textLocked}
        onChange={(e) => setText(e.target.value)}
        helperText={s.textLocked ? 'The voice library is changed in the AI tab; approve it here.' : `${text.length.toLocaleString()} characters`}
      />
      {error && <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="info" sx={{ mt: 1 }} onClose={() => setNotice(null)}>{notice}</Alert>}
      <Box sx={{ display: 'flex', gap: 1, mt: 1, justifyContent: 'flex-end' }}>
        {edited && <Button size="small" disabled={busy} onClick={() => setText(s.text)}>Undo my changes</Button>}
        {staffEdit && s.status === 'draft' && (
          <Button size="small" color="inherit" disabled={busy} onClick={() => act('reject')}>
            <InfoTip tip="Not a good example (too specific to one patient, or the edit was a one-off). The AI will not use it.">Set aside</InfoTip>
          </Button>
        )}
        {s.status === 'draft' && (
          <Button size="small" variant="outlined" disabled={busy || !edited} onClick={() => act('save')}>
            <InfoTip tip="Keeps your edit on the draft without approving it.">Save changes</InfoTip>
          </Button>
        )}
        <Button size="small" variant="contained" color="success" startIcon={<ApproveIcon />} disabled={busy || (s.status === 'approved' && !edited)} onClick={() => act('approve')}>
          <InfoTip tip="Makes this text live: the AI uses it from now on. It shows as approved in Skills review under your name, and a Levitan admin can roll it back.">
            {busy ? 'Working…' : 'Approve'}
          </InfoTip>
        </Button>
      </Box>
    </Paper>
  );
};

const ReviewPanel: React.FC<{ review: AiRatingReview | null; loading: boolean; error: string | null; reload: () => void }> = ({ review, loading, error, reload }) => {
  const topic = review?.topicDrafts || [];
  const practice = review?.practice || [];
  const edits = review?.editCandidates || [];
  return (
    <Box sx={{ mb: 3 }}>
      {error && <Alert severity="error" sx={{ mb: 1 }} action={<Button size="small" onClick={reload}>Try again</Button>}>{error}</Alert>}
      <Accordion disableGutters defaultExpanded={edits.length > 0}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <InfoTip tip="When staff change an AI draft before texting it from Ask Levitan AI, the reply they actually sent lands here. Approve it and the AI uses it as an example of how the practice answers; set it aside if it is not a good example.">
            <Typography sx={{ fontWeight: 600 }}>Staff-edited replies waiting for approval ({loading && !review ? '…' : edits.length})</Typography>
          </InfoTip>
        </AccordionSummary>
        <AccordionDetails>
          {edits.length === 0 && <Typography variant="body2" color="text.secondary">Nothing is waiting.</Typography>}
          {edits.map((s) => <SkillCard key={`${s.id}-${s.version}`} s={s} onDone={reload} />)}
        </AccordionDetails>
      </Accordion>
      <Accordion disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <InfoTip tip="Topic skills tell the AI how the practice handles one kind of message. These drafts change nothing until approved.">
            <Typography sx={{ fontWeight: 600 }}>Topic skills waiting for approval ({loading && !review ? '…' : topic.length})</Typography>
          </InfoTip>
        </AccordionSummary>
        <AccordionDetails>
          {topic.length === 0 && <Typography variant="body2" color="text.secondary">Nothing is waiting.</Typography>}
          {topic.map((s) => <SkillCard key={`${s.id}-${s.version}`} s={s} onDone={reload} />)}
        </AccordionDetails>
      </Accordion>
      <Accordion disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <InfoTip tip="How the AI writes (writing style), how it shortens long records (compression) and the clinic's own facts (clinic knowledge). Shows the version waiting for approval, or the approved one.">
            <Typography sx={{ fontWeight: 600 }}>Writing style, compression and clinic knowledge ({loading && !review ? '…' : practice.length})</Typography>
          </InfoTip>
        </AccordionSummary>
        <AccordionDetails>
          {practice.length === 0 && <Typography variant="body2" color="text.secondary">Nothing to show.</Typography>}
          {practice.map((s) => <SkillCard key={`${s.id}-${s.version}`} s={s} onDone={reload} />)}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

const ExampleApprove: React.FC<{ sheet: string; c: { n: number; A: string; B: string }; approved?: Examples[string]; onDone: () => void }> = ({ sheet, c, approved, onDone }) => {
  const [open, setOpen] = useState(false);
  const [side, setSide] = useState<'A' | 'B' | ''>('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const choose = (v: 'A' | 'B') => {
    setSide(v);
    setText(c[v]);
  };

  const approve = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await draftRatingApi.approveCase(sheet, c.n, text);
      const removed = res.data.removed || [];
      setNotice(`Approved as an example reply.${removed.length ? ` Removed before saving: ${removed.join(', ')}.` : ''}`);
      setOpen(false);
      onDone();
    } catch (err: any) {
      setError(errorText(err, 'The example could not be approved.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {approved && (
          <Chip size="small" color="success" label={`Approved as an example reply${approved.reviewedBy ? ` by ${approved.reviewedBy.replace(/ \(AI Rating\)$/, '')}` : ''}`} />
        )}
        <Button size="small" variant={open ? 'text' : 'outlined'} onClick={() => setOpen((x) => !x)}>
          <InfoTip tip="Pick a draft, edit it until it is the reply you would send, and approve it. The AI then uses it as an example of how the practice answers this kind of message. The patient's name, phone numbers and dates are removed.">
            {open ? 'Close' : approved ? 'Approve a new example reply' : 'Approve as an example reply'}
          </InfoTip>
        </Button>
      </Box>
      {notice && <Alert severity="success" sx={{ mt: 1 }} onClose={() => setNotice(null)}>{notice}</Alert>}
      {open && (
        <Box sx={{ mt: 1 }}>
          <RadioGroup row value={side} onChange={(e) => choose(e.target.value as 'A' | 'B')}>
            <FormControlLabel value="A" control={<Radio size="small" />} label="Start from draft A" />
            <FormControlLabel value="B" control={<Radio size="small" />} label="Start from draft B" />
          </RadioGroup>
          {side && (
            <>
              <TextField fullWidth multiline minRows={4} maxRows={14} size="small" value={text} disabled={busy}
                onChange={(e) => setText(e.target.value)} inputProps={{ maxLength: 4000 }}
                helperText="Edit it into the reply you would send. This exact text becomes the example." />
              {error && <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>{error}</Alert>}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button size="small" variant="contained" color="success" startIcon={<ApproveIcon />} disabled={busy || text.trim().length < 10} onClick={approve}>
                  {busy ? 'Approving…' : 'Approve example reply'}
                </Button>
              </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

const RatingSheet: React.FC<Props & { examples: Examples; onExample: () => void }> = ({ initial, examples, onExample }) => {
  const [answers, setAnswers] = useState<Record<string, string>>(initial.answers || {});
  const [finishedAt, setFinishedAt] = useState<string | null>(initial.finishedAt);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<DraftRatingResults | null>(null);
  const [loadingResults, setLoadingResults] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef(answers);
  const cases = initial.cases;
  const sheet = initial.sheet;
  const locked = !!finishedAt;
  const answered = cases.filter((c) => answers[`c${c.n}_pref`]).length;

  const save = useCallback(async (next: Record<string, string>, finished = false) => {
    setSaving(true);
    setError(null);
    try {
      const res = await draftRatingApi.save(sheet, next, finished);
      setSavedAt(res.data.savedAt);
      if (res.data.finishedAt) setFinishedAt(res.data.finishedAt);
    } catch (err: any) {
      setError(errorText(err, 'Your answers could not be saved.'));
    } finally {
      setSaving(false);
    }
  }, [sheet]);

  useEffect(() => () => {
    if (timer.current) {
      clearTimeout(timer.current);
      draftRatingApi.save(sheet, latest.current).catch(() => undefined);
    }
  }, [sheet]);

  const setAnswer = (key: string, value: string) => {
    if (locked) return;
    const next = { ...answers, [key]: value };
    setAnswers(next);
    latest.current = next;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { timer.current = null; save(next); }, 800);
  };

  const finish = async () => {
    if (!window.confirm('Finish the sheet? Your answers will be locked and can no longer be changed.')) return;
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    await save(answers, true);
  };

  const loadResults = async () => {
    setLoadingResults(true);
    setError(null);
    try {
      const res = await draftRatingApi.results(sheet);
      setResults(res.data);
    } catch (err: any) {
      setError(errorText(err, 'The results could not be loaded.'));
    } finally {
      setLoadingResults(false);
    }
  };

  const sendGroup = (n: number, side: 'A' | 'B') => (
    <RadioGroup row value={answers[`c${n}_${side}`] || ''} onChange={(e) => setAnswer(`c${n}_${side}`, e.target.value)}>
      {SEND_OPTIONS.map(([v, l, tip]) => (
        <FormControlLabel
          key={v}
          value={v}
          disabled={locked}
          control={<Radio size="small" />}
          label={<InfoTip tip={tip}>{l}</InfoTip>}
        />
      ))}
    </RadioGroup>
  );

  const counts = (m: Record<string, number>) =>
    SEND_OPTIONS.map(([v]) => `${SEND_LABELS[v]} ${m[v] || 0}`).join(' · ');

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {cases.length} real patient messages from August to October 2026. {initial.intro} You are not told which is
        which. For each draft, choose what you would need to do before sending it, then pick the one you would rather
        send. Answers save as you go; press Finish when every case has a choice. Confidential: these are real patient
        messages.
      </Typography>

      <Box sx={{ position: 'sticky', top: 0, zIndex: 2, bgcolor: 'background.paper', py: 1, mb: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2">
            <InfoTip tip="Cases where you have picked the draft you would rather send.">{answered} of {cases.length} answered</InfoTip>
          </Typography>
          <Box sx={{ flex: 1, minWidth: 120 }}>
            <LinearProgress variant="determinate" value={cases.length ? (100 * answered) / cases.length : 0} />
          </Box>
          <Typography variant="caption" color="text.secondary">
            {saving ? 'Saving…' : locked ? `Finished ${new Date(finishedAt as string).toLocaleString()}` : savedAt ? `Saved ${new Date(savedAt).toLocaleTimeString()}` : ''}
          </Typography>
          {!locked && (
            <Button variant="contained" size="small" disabled={saving || answered < cases.length} onClick={finish}>
              <InfoTip tip="Locks your answers once every case has a choice. Until then your answers are saved and you can come back later.">Finish</InfoTip>
            </Button>
          )}
          {initial.canSeeResults && (
            <Button variant="outlined" size="small" disabled={loadingResults} onClick={loadResults}>
              <InfoTip tip="Superusers only: shows each rater's progress and which system their choices favoured. Do not show this to a rater before they finish.">Show results</InfoTip>
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {locked && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Thank you. Your answers are locked and have been recorded.
        </Alert>
      )}

      {results && (
        <TableContainer sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><InfoTip tip="The signed-in user who rated.">Rater</InfoTip></TableCell>
                <TableCell><InfoTip tip="Cases with a preferred draft, out of the total.">Answered</InfoTip></TableCell>
                <TableCell><InfoTip tip={`How often the rater preferred the ${results.labels.ours} draft, the ${results.labels.other} draft, or neither.`}>Preferred</InfoTip></TableCell>
                <TableCell><InfoTip tip={`What the rater said the ${results.labels.ours} drafts needed before sending.`}>{results.labels.ours}</InfoTip></TableCell>
                <TableCell><InfoTip tip={`What the rater said the ${results.labels.other} drafts needed before sending.`}>{results.labels.other}</InfoTip></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.raters.length === 0 && (
                <TableRow><TableCell colSpan={5}>No one has rated yet.</TableCell></TableRow>
              )}
              {results.raters.map((r) => (
                <TableRow key={r.rater}>
                  <TableCell>{r.rater}{r.finishedAt ? '' : ' (in progress)'}</TableCell>
                  <TableCell>{r.answered} / {r.total}</TableCell>
                  <TableCell>{results.labels.ours} {r.preferred.ours} · {results.labels.other} {r.preferred.other} · Same {r.preferred.tie}</TableCell>
                  <TableCell>{counts(r.ours)}</TableCell>
                  <TableCell>{counts(r.other)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {cases.map((c) => (
        <Paper key={c.n} variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Case {c.n} <Typography component="span" variant="caption" color="text.secondary">{c.date}</Typography>
          </Typography>
          <Box sx={{ bgcolor: 'action.hover', p: 1.5, borderRadius: 1, whiteSpace: 'pre-wrap', mb: 1.5 }}>
            <b>Patient:</b> {c.message}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
            {(['A', 'B'] as const).map((side) => (
              <Box key={side} sx={{ flex: 1, border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                <Typography variant="subtitle2" color="text.secondary">Draft {side}</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', my: 1 }}>{c[side]}</Typography>
                {sendGroup(c.n, side)}
              </Box>
            ))}
          </Box>
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              <InfoTip tip="The draft you would rather start from when replying to this patient.">Which would you rather send?</InfoTip>
            </Typography>
            <RadioGroup row value={answers[`c${c.n}_pref`] || ''} onChange={(e) => setAnswer(`c${c.n}_pref`, e.target.value)}>
              <FormControlLabel value="A" disabled={locked} control={<Radio size="small" />} label="A" />
              <FormControlLabel value="B" disabled={locked} control={<Radio size="small" />} label="B" />
              <FormControlLabel value="tie" disabled={locked} control={<Radio size="small" />} label="About the same" />
            </RadioGroup>
            <TextField
              fullWidth
              size="small"
              disabled={locked}
              label={<InfoTip tip="Anything worth knowing about this case, such as what was wrong with a draft. Up to 1,000 characters.">Note (optional)</InfoTip>}
              value={answers[`c${c.n}_note`] || ''}
              onChange={(e) => setAnswer(`c${c.n}_note`, e.target.value)}
              inputProps={{ maxLength: 1000 }}
            />
          </Box>
          <ExampleApprove sheet={sheet} c={c} approved={examples[`example_rating_s${sheet}_c${c.n}`]} onDone={onExample} />
        </Paper>
      ))}
      {saving && <CircularProgress size={16} />}
    </>
  );
};

const DraftRatingTab: React.FC<Props> = ({ initial }) => {
  const [state, setState] = useState<DraftRatingState>(initial);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [review, setReview] = useState<AiRatingReview | null>(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const sheets = state.sheets || [];

  const loadReview = useCallback(async () => {
    setReviewLoading(true);
    setReviewError(null);
    try {
      setReview((await draftRatingApi.review()).data);
    } catch (err: any) {
      setReviewError(errorText(err, 'The items waiting for approval could not be loaded.'));
    } finally {
      setReviewLoading(false);
    }
  }, []);

  useEffect(() => { loadReview(); }, [loadReview]);

  const openSheet = async (id: string | null) => {
    if (!id || id === state.sheet) return;
    setLoading(true);
    setError(null);
    try {
      const res = await draftRatingApi.get(id);
      setState(res.data);
    } catch (err: any) {
      setError(errorText(err, 'That sheet could not be loaded.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        <InfoTip tip="Rate AI reply drafts blind, and approve what the AI uses: topic skills, writing style, compression, clinic knowledge and example replies. Everything approved here shows as approved in Skills review under your name.">AI Rating (dev only)</InfoTip>
      </Typography>
      <Typography variant="body1" sx={{ mb: 2 }}>
        Clinic Staff carries out the appropriate ratings on this page.
      </Typography>
      <ReviewPanel review={review} loading={reviewLoading} error={reviewError} reload={loadReview} />
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        <InfoTip tip="Blind rating sheets. Each case can also be approved as an example reply.">Rating sheets</InfoTip>
      </Typography>
      {sheets.length > 1 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <ToggleButtonGroup size="small" exclusive value={state.sheet} disabled={loading} onChange={(_, v) => openSheet(v)}>
            {sheets.map((s) => (
              <ToggleButton key={s.id} value={s.id}>
                <InfoTip tip={`${s.total} cases. Each sheet is answered and finished separately.`}>
                  {s.label}{s.finishedAt ? ' (finished)' : ''}
                </InfoTip>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {loading && <CircularProgress size={16} />}
        </Box>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <RatingSheet key={state.sheet} initial={state} examples={review?.examples || {}} onExample={loadReview} />
    </Paper>
  );
};

export default DraftRatingTab;
